import {
  BulkActionContainerCol,
  Button,
  Col,
  PlusIcon,
  Table,
  TableActionBarRow,
  ValueOrFallback,
} from "melco-ui";

import { useTranslation } from "react-i18next";
import {
  BulkActionCount,
  FormattedDate,
  ReportingMelcoMachineStatisticsModelsGroupListItem,
  Search,
  TableCellLink,
  useTableSelection,
} from "melco-shared-logic";
import { useMachineGroups } from "../../../hooks/reporting/useMachineGroups";
import { useAddRemoveMachineGroups } from "../../../hooks/reporting/useAddRemoveMachineGroup";
import { useFormikContext } from "formik";
import { BulkActionAddAndSaveMachineGroups } from "./BulkActionAddAndSaveMachineGroups";

export type MachineGroupsListProps = {};

const DETAIL_VIEW_PATH = "/reporting/machine/groups";

export const MachineStatusAddAndSaveMachineGroupsList: React.FC<
  MachineGroupsListProps
> = ({}) => {
  const { t } = useTranslation();

  const {
    data,
    errorComponent,
    isLoading,
    onTableConfigChange,
    searchQueryChange,
  } = useMachineGroups();

  const { submitForm } = useFormikContext();

  const { isAlreadyAdded, addGroups, removeGroups } =
    useAddRemoveMachineGroups();

  const columns = [
    {
      title: t("reporting.machinestatus.view.machinegroups.list.columns.name"),
      key: "name",
      dataIndex: "name",
      sorter: true,

      render: (
        _: string,
        item: ReportingMelcoMachineStatisticsModelsGroupListItem
      ) => (
        <ValueOrFallback
          value={item?.name}
          render={(value) => (
            <TableCellLink to={[DETAIL_VIEW_PATH, item.id].join("/")}>
              {value}
            </TableCellLink>
          )}
          fallback={t("global.fallback.empty")}
        />
      ),
    },
    {
      title: t(
        "reporting.machinestatus.view.machinegroups.list.columns.machines"
      ),
      key: "machine_count",
      dataIndex: "machine_count",
      sorter: true,
    },
    {
      title: t(
        "reporting.machinestatus.view.machinegroups.list.columns.created"
      ),
      key: "created_date",
      dataIndex: "created_date",
      sorter: true,
      render: (
        _: string,
        item: ReportingMelcoMachineStatisticsModelsGroupListItem
      ) => <FormattedDate date={item.created_date} />,
    },
    {
      title: t(
        "reporting.machinestatus.view.machinegroups.list.columns.last_modified"
      ),
      key: "updated_date",
      dataIndex: "updated_date",
      sorter: true,
      render: (
        _: string,
        item: ReportingMelcoMachineStatisticsModelsGroupListItem
      ) => <FormattedDate date={item.updated_date} />,
    },

    {
      title: "",
      render: (rowObj: ReportingMelcoMachineStatisticsModelsGroupListItem) => {
        return isAlreadyAdded(rowObj) ? (
          <Button onClick={() => removeGroups([{ id: rowObj.id! }])}>
            {t(
              "reporting.machinestatus.view.machinegroups.list.columns.remove"
            )}
          </Button>
        ) : (
          <Button
            type="primary"
            icon={<PlusIcon />}
            onClick={() => {
              addGroups([rowObj]);
              submitForm();
            }}
          >
            {t("reporting.machinestatus.view.machinegroups.list.columns.add")}
          </Button>
        );
      },
    },
  ];

  const { selectedRowKeys, setSelectedRowKeys, rowSelection, onRow } =
    useTableSelection<ReportingMelcoMachineStatisticsModelsGroupListItem>(
      () => {}
    );

  return (
    <>
      <TableActionBarRow>
        <Col>
          <Search
            onSearchInputChange={(query: string) => searchQueryChange(query)}
          />
        </Col>
        <BulkActionContainerCol>
          <BulkActionCount selectedCount={selectedRowKeys.length} />
          <BulkActionAddAndSaveMachineGroups
            allMachineGroups={data}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
          />
        </BulkActionContainerCol>
      </TableActionBarRow>

      <Table
        dataSource={data}
        columns={columns}
        rowKey="name"
        onChange={onTableConfigChange}
        onRow={onRow}
        rowSelection={rowSelection}
        errorComponent={errorComponent}
        loading={isLoading}
        noDataMessage={t("reporting.machinegroups.list.empty")}
      />
    </>
  );
};
